import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Row, Col, Input, Label, Button, FormFeedback } from "reactstrap";
import {
  createSyllabus,
  fetchDays,
  addWeeklySyllabusThunk,
  addSyllabusDetailThunk,
} from "slices/Course/thunk";
import { TOTAL_SYLLABUS_WEEKS } from "./coursehelper";
import { AppDispatch } from "store";

interface SyllabusFormProps {
  validation: any;
  isEdit?: boolean;
}

interface WeekRow {
  uiId: number;
  weekid?: number | null;
  title: string;
  keydeliverable: string;
  weekname?: string;
  saved?: boolean;
  open?: boolean;
  weekdetailId?: number | null;
  days: DayRow[];
  dayForm: {
    dayid?: number | null;
    dayname?: string;
    title: string;
    description: string;
  };
}

interface DayRow {
  uiId: number;
  dayid?: number | null;
  dayname?: string;
  title: string;
  description: string;
}

const SyllabusForm: React.FC<SyllabusFormProps> = ({
  validation,
  isEdit = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [hydratedExisting, setHydratedExisting] = useState(false);

  const [saving, setSaving] = useState(false);
  const [savingWeek, setSavingWeek] = useState(false);
  const [savingDay, setSavingDay] = useState(false);
  const [syllabusId, setSyllabusId] = useState<number | null>(null);
  const [syllabusSaved, setSyllabusSaved] = useState(false);
  const [daysFetched, setDaysFetched] = useState(false);

  const [addedWeeks, setAddedWeeks] = useState<WeekRow[]>([]);

  const weeklyLoading = useSelector(
    (state: any) => state.course.weeklySyllabusLoading
  );

  const days = useSelector((state: any) => state.course.dayData);
  const dayLoading = useSelector((state: any) => state.course.dayLoading);

  const normalizeNumber = (value: any) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const serializeWeeksForFormik = (weeksArr: WeekRow[]) =>
    weeksArr.map((w) => ({
      weekid: w.weekid,
      title: w.title,
      keydeliverable: w.keydeliverable,
      weekname: w.weekname,
      saved: w.saved ?? false, 
      open: w.open,
      weekdetailId: w.weekdetailId,
      days: (w.days || []).map((d) => ({
        dayid: d.dayid,
        dayname: d.dayname,
        title: d.title,
        description: d.description,
      })),
    }));

  const hydrateWeeksFromFormik = (items: any[] = []): WeekRow[] =>
    items.map((w, idx) => {
      const hydratedDays =
        w.days ||
        w.detail ||
        w.syllabusdetail ||
        w.syllabus_detail ||
        [];

      return {
        uiId: Date.now() + idx,
        weekid:
          normalizeNumber(
            w.weekid ??
              w.week_id ??
              w.id ??
              w.weekdetail_id ??
              w.weekdetailId
          ) || undefined,
        title: w.title || w.week_title || "",
        keydeliverable:
          w.keydeliverable || w.week_keydeliverable || w.key_deliverables || "",
        weekname: w.weekname || w.week_name || w.name || "",
        saved: true,
        open: idx === items.length - 1,
        weekdetailId:
          normalizeNumber(
            w.weekdetailId ??
              w.weekdetail_id ??
              w.weekdetailid ??
              w.week_id ??
              w.id
          ) || null,
        days: hydratedDays.map((d: any, dIdx: number) => ({
          uiId: Date.now() + idx * 100 + dIdx,
          dayid: normalizeNumber(d.dayid ?? d.id) || undefined,
          dayname: d.dayname || d.day || d.name || "",
          title: d.title || "",
          description: d.description || d.Description || "",
        })),
        dayForm: {
          dayid: null,
          dayname: "",
          title: "",
          description: "",
        },
      };
    });

  useEffect(() => {
    const existingId =
      validation?.values?.syllabus_id ||
      validation?.values?.syllabus_items?.[0]?.syllabus_id ||
      validation?.values?.syllabus_items?.[0]?.id ||
      null;

    if (existingId && syllabusId !== Number(existingId)) {
      setSyllabusId(Number(existingId));
      setSyllabusSaved(true);
    }
  }, [
    syllabusId,
    validation?.values?.syllabus_id,
    validation?.values?.syllabus_items,
  ]);

  useEffect(() => {
    if (syllabusSaved && !daysFetched) {
      if (!days?.length) dispatch(fetchDays());
      setDaysFetched(true);
    }
  }, [dispatch, syllabusSaved, daysFetched, days?.length]);

  useEffect(() => {
    if (!syllabusSaved || hydratedExisting || addedWeeks.length > 0) return;
    const formWeeks = validation?.values?.syllabus_items;
    if (Array.isArray(formWeeks) && formWeeks.length) {
      setAddedWeeks(hydrateWeeksFromFormik(formWeeks));
      setHydratedExisting(true);
    }
  }, [syllabusSaved, hydratedExisting, addedWeeks.length, validation?.values?.syllabus_items]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validation.values.course_id) {
      toast.error("Please complete Basic Info first.");
      return;
    }

    const payload = {
      course_id: validation.values.course_id,
      key_deliverables: validation.values.key_deliverables,
      daywise_description: validation.values.daywise_description,
    };

    try {
      setSaving(true);
      const response = await dispatch(createSyllabus(payload) as any);

      if (response?.success) {
        const returnedId =
          response?.data?.id || response?.data || syllabusId || null;

        if (returnedId) {
          setSyllabusId(returnedId);
          validation.setFieldValue("syllabus_id", returnedId);
        }

        setSyllabusSaved(true);
        toast.success("Syllabus saved successfully");
      } else {
        toast.error(response?.message || "Failed to save syllabus");
      }
    } finally {
      setSaving(false);
    }
  };

  const buildEmptyWeekRow = (): WeekRow => ({
    uiId: Date.now() + Math.floor(Math.random() * 1000),
    weekid: undefined,
    title: "",
    keydeliverable: "",
    weekname: "",
    saved: false,
    open: true,
    weekdetailId: null,
    days: [],
    dayForm: {
      dayid: null,
      dayname: "",
      title: "",
      description: "",
    },
  });

  const appendEmptyWeekRow = () => {
    setAddedWeeks((prev) => {
      const next = [...prev, buildEmptyWeekRow()];
      validation?.setFieldValue?.(
        "syllabus_items",
        serializeWeeksForFormik(next)
      );
      // Accordion: only the newly added week stays expanded
      return next.map((w, i) => ({
        ...w,
        open: i === next.length - 1,
      }));
    });
  };

  /** Toggle week expand/collapse (accordion: only one open at a time) */
  const toggleWeekOpen = (index: number) => {
    setAddedWeeks((prev) =>
      prev.map((w, i) => ({
        ...w,
        open: i === index ? !w.open : false,
      }))
    );
  };

  const handleAddWeekRow = async () => {
    if (!syllabusSaved || !syllabusId) {
      toast.error("Please save syllabus first.");
      return;
    }
    appendEmptyWeekRow();
  };

  const handleSaveWeekRow = async (
    week: WeekRow,
    index: number,
    options?: { silent?: boolean }
  ) => {
    if (!syllabusId) {
      toast.error("Please save syllabus first.");
      return false;
    }

    if (!week.weekid || !week.title.trim() || !week.keydeliverable.trim()) {
      toast.error("Please fill all week fields.");
      return false;
    }

    const payload = {
      weekid: Number(week.weekid),
      syllabus_id: syllabusId,
      title: week.title,
      keydeliverable: week.keydeliverable,
    };

    try {
      setSavingWeek(true);
      const response = await dispatch(addWeeklySyllabusThunk(payload) as any);

      if (response?.success) {
        if (!options?.silent) toast.success("Week added successfully");

        const rawWeekDetailId =
          response?.data?.weekdetail_id ??
          response?.data?.weekdetailId ??
          response?.data?.week_id ??
          response?.data?.weekId ??
          response?.data?.weekid ??
          response?.data?.id ??
          response?.data;

        const parsedWeekDetailId = Number(rawWeekDetailId);
        const fallbackWeekId = week.weekid ? Number(week.weekid) : null;
        const effectiveWeekDetailId =
          !Number.isNaN(parsedWeekDetailId) && parsedWeekDetailId
            ? parsedWeekDetailId
            : fallbackWeekId;

        if (!effectiveWeekDetailId) {
          toast.error("Could not read week id from server response");
          return false;
        }

        if (
          Number.isNaN(parsedWeekDetailId) ||
          !parsedWeekDetailId ||
          parsedWeekDetailId === null
        ) {
          toast.warn("Week saved, using selected week id for linking days.");
        }

        setAddedWeeks((prev) => {
          const currentWeekFromState = prev[index];
          
          const updatedWeeks = prev.map((item, idx) => {
            if (idx === index) {
              return {
                uiId: currentWeekFromState.uiId,
                weekid: currentWeekFromState.weekid,
                weekname: currentWeekFromState.weekname || "",
                title: currentWeekFromState.title || "",
                keydeliverable: currentWeekFromState.keydeliverable || "",
                days: currentWeekFromState.days || [],
                dayForm: currentWeekFromState.dayForm || {
                  dayid: null,
                  dayname: "",
                  title: "",
                  description: "",
                },
                saved: true,
                open: true,
                weekdetailId: Number(effectiveWeekDetailId),
              };
            }
            return item;
          });
          
          validation?.setFieldValue?.(
            "syllabus_items",
            serializeWeeksForFormik(updatedWeeks)
          );
          
          return updatedWeeks;
        });
        return true;
      } else {
        toast.error(response?.message || "Failed to save week");
        return false;
      }
    } finally {
      setSavingWeek(false);
    }
  };

  const handleSaveDayForWeek = async (weekIndex: number) => {
    const week = addedWeeks[weekIndex];
    
    const targetWeekdetailId = week?.weekdetailId 
      ? Number(week.weekdetailId) 
      : null;

    if (!targetWeekdetailId || isNaN(targetWeekdetailId)) {
      toast.error("Please save the week first before adding days.");
      return;
    }

    const { dayid, title, description } = week.dayForm;

    if (!dayid || !title.trim() || !description.trim()) {
      toast.error("Please fill all day fields.");
      return;
    }

    const payload = {
      weekdetail_id: Number(targetWeekdetailId), 
      dayid: Number(dayid),
      title: title.trim(),
      Description: description.trim(),
    };

    try {
      setSavingDay(true);
      const response = await dispatch(addSyllabusDetailThunk(payload) as any);

      if (response?.success !== false) {
        toast.success("Day added");

        const selected = days?.find(
          (d: any) => Number(d.id) === Number(dayid)
        );
        const newDay: DayRow = {
          uiId: Date.now() + Math.floor(Math.random() * 1000),
          dayid: Number(dayid),
          dayname: selected?.dayname || "",
          title: title.trim(),
          description: description.trim(),
        };

        setAddedWeeks((prev) =>
          prev.map((item, idx) => {
            if (idx === weekIndex) {
              // Add the new day and clear the Add Day form
              return {
                ...item,
                days: [...(item.days || []), newDay],
                dayForm: {
                  dayid: null,
                  dayname: "",
                  title: "",
                  description: "",
                },
              };
            }
            return item;
          })
        );

        setAddedWeeks((prev) => {
          const copy = [...prev];
          validation?.setFieldValue?.(
            "syllabus_items",
            serializeWeeksForFormik(copy)
          );
          return copy;
        });
      } else {
        toast.error(response?.message || "Failed to save day");
      }
    } finally {
      setSavingDay(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {!syllabusSaved && (
        <div className="border rounded p-3 mb-4 shadow-sm">
          <Row className="g-3">
            <Col md={12}>
              <Label>Key Deliverables <span className="text-danger">*</span></Label>
              <Input
                type="text"
                name="key_deliverables"
                value={validation.values.key_deliverables}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.key_deliverables &&
                  !!validation.errors.key_deliverables
                }
                disabled={isEdit}
              />
              <FormFeedback>
                {validation.errors.key_deliverables}
              </FormFeedback>
            </Col>

            <Col md={12}>
              <Label>Day-wise Description <span className="text-danger">*</span></Label>
              <Input
                type="textarea"
                rows={3}
                name="daywise_description"
                value={validation.values.daywise_description}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.daywise_description &&
                  !!validation.errors.daywise_description
                }
                disabled={isEdit}
              />
              <FormFeedback>
                {validation.errors.daywise_description}
              </FormFeedback>
            </Col>
          </Row>
        </div>
      )}
      {syllabusSaved && (
        <div className="border rounded p-3 mb-4 shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Weekly Syllabus</h6>
            <Button color="dark" size="sm" outline onClick={handleAddWeekRow}>
              + Add Week
            </Button>
          </div>

          {addedWeeks.map((week, index) => {
            const isOpen =
              week.open === true ||
              (addedWeeks.length === 1 && week.open !== false);
            const isSaved = week.saved === true && week.weekdetailId !== null && week.weekdetailId !== undefined;

            return (
              <div
                key={week.uiId}
                className="border rounded mb-2 overflow-hidden"
                style={{ background: "#fafafa" }}
              >
                <div
                  className="d-flex justify-content-between align-items-center p-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleWeekOpen(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleWeekOpen(index);
                    }
                  }}
                  aria-expanded={isOpen}
                  aria-controls={`week-content-${week.uiId}`}
                >
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <i
                      className={`ri-${isOpen ? "arrow-up-s" : "arrow-down-s"}-line me-1`}
                      style={{ fontSize: "1.1rem" }}
                      aria-hidden
                    />
                    <strong>
                      {week.weekname
                        ? week.weekname
                        : `Week ${index + 1}`}
                    </strong>
                    {isSaved && (
                      <span className="badge bg-success ms-2">Saved</span>
                    )}
                    {week.days?.length ? (
                      <span className="badge bg-secondary ms-1">
                        {week.days.length} day{week.days.length > 1 ? "s" : ""}
                      </span>
                    ) : null}
                  </div>
                </div>

                {isOpen && (
                  <div id={`week-content-${week.uiId}`} className="px-2 pb-2 pt-0 mt-3">
                    <Row className="g-3">
                      <Col md={12}>
                        <Label>Select Week <span className="text-danger">*</span></Label>
                        <Input
                          type="select"
                          value={week.weekid ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const weekNum = value ? Number(value) : 0;
                            setAddedWeeks((prev) =>
                              prev.map((item, idx) =>
                                idx === index
                                  ? {
                                      ...item,
                                      weekid: weekNum || undefined,
                                      weekname: weekNum ? `Week ${weekNum}` : "",
                                    }
                                  : item
                              )
                            );
                          }}
                          disabled={isSaved}
                          readOnly={isSaved}
                        >
                          <option value="">Select Week</option>
                          {(() => {
                            const usedByOther = (wId: number) =>
                              addedWeeks.some(
                                (aw, idx) =>
                                  idx !== index &&
                                  Number(aw.weekid) === Number(wId)
                              );
                            return Array.from({ length: TOTAL_SYLLABUS_WEEKS }, (_, i) => i + 1)
                              .filter(
                                (wId) =>
                                  !usedByOther(wId) ||
                                  Number(week.weekid) === wId
                              )
                              .map((wId) => (
                                <option key={wId} value={wId}>
                                  Week {wId}
                                </option>
                              ));
                          })()}
                        </Input>
                      </Col>

                      <Col md={12}>
                        <Label>Title <span className="text-danger">*</span></Label>
                        <Input
                          type="text"
                          value={week.title || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAddedWeeks((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, title: value } : item
                              )
                            );
                          }}
                          disabled={isSaved}
                          readOnly={isSaved}
                        />
                      </Col>

                      <Col md={12}>
                        <Label>Key Deliverable <span className="text-danger">*</span></Label>
                        <Input
                          type="textarea"
                          rows={3}
                          value={week.keydeliverable || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAddedWeeks((prev) =>
                              prev.map((item, idx) =>
                                idx === index
                                  ? { ...item, keydeliverable: value }
                                  : item
                              )
                            );
                          }}
                          disabled={isSaved}
                          readOnly={isSaved}
                        />
                      </Col>

                      <Col md={12} className="text-end">
                        <Button
                          color="primary"
                          onClick={() => handleSaveWeekRow(week, index)}
                          disabled={
                            savingWeek ||
                            weeklyLoading ||
                            !week.weekid ||
                            !week.title.trim() ||
                            !week.keydeliverable.trim()
                          }
                        >
                          {(savingWeek || weeklyLoading) && (
                            <i className="fa fa-spinner fa-spin me-2" />
                          )}
                          Save Week
                        </Button>
                      </Col>
                    </Row>

                    <div className="mt-4 border-top pt-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Add Day</h6>
                        <small className="text-muted">
                          {isSaved 
                            ? "Days are linked to this week" 
                            : "Save week first to add days"}
                        </small>
                      </div>

                      <Row className="g-3">
                        <Col md={12}>
                          <Label>Select Day <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            value={week.dayForm.dayid ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const selectedDay = days?.find(
                                (d: any) => Number(d.id) === Number(value)
                              );

                              setAddedWeeks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? {
                                        ...item,
                                        dayForm: {
                                          ...item.dayForm,
                                          dayid: value
                                            ? Number(value)
                                            : null,
                                          dayname:
                                            selectedDay?.dayname || "",
                                        },
                                      }
                                    : item
                                )
                              );
                            }}
                          >
                            <option value="">Select Day</option>
                            {days?.map((day: any) => (
                              <option key={day.id} value={day.id}>
                                {day.dayname}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={12}>
                          <Label>Title <span className="text-danger">*</span></Label>
                          <Input
                            type="text"
                            value={week.dayForm.title}
                            onChange={(e) => {
                              const value = e.target.value;
                              setAddedWeeks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? {
                                        ...item,
                                        dayForm: {
                                          ...item.dayForm,
                                          title: value,
                                        },
                                      }
                                    : item
                                )
                              );
                            }}
                          />
                        </Col>

                        <Col md={12}>
                          <Label>Key Deliverable <span className="text-danger">*</span></Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={week.dayForm.description}
                            onChange={(e) => {
                              const value = e.target.value;
                              setAddedWeeks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? {
                                        ...item,
                                        dayForm: {
                                          ...item.dayForm,
                                          description: value,
                                        },
                                      }
                                    : item
                                )
                              );
                            }}
                          />
                        </Col>

                        <Col md={12} className="text-end">
                          <Button
                            color="primary"
                            onClick={() => handleSaveDayForWeek(index)}
                            disabled={
                              savingDay ||
                              dayLoading ||
                              !week.dayForm.dayid ||
                              !week.dayForm.title.trim() ||
                              !week.dayForm.description.trim()
                            }
                          >
                            {(savingDay || dayLoading) && (
                              <i className="fa fa-spinner fa-spin me-2" />
                            )}
                            Save Day
                          </Button>
                        </Col>
                      </Row>

                      {week.days?.length > 0 && (
                        <div className="mt-3">
                          <div className="d-flex flex-column gap-2">
                            {week.days.map((day) => (
                              <div
                                key={day.uiId}
                                className="border rounded p-2 bg-white"
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="fw-semibold">
                                    {day.dayname || "Day"}
                                  </span>
                                  {day.dayid && (
                                    <small className="text-muted">
                                      ID: {day.dayid}
                                    </small>
                                  )}
                                </div>
                                <div className="small mt-1">
                                  <div>
                                    <strong>Title:</strong> {day.title}
                                  </div>
                                  <div>
                                    <strong>Key Deliverable:</strong>{" "}
                                    {day.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!syllabusSaved && (
        <div className="d-flex justify-content-end mt-4">
          <Button
            color="primary"
            type="submit"
            disabled={
              saving ||
              !validation.values.key_deliverables ||
              !validation.values.daywise_description
            }
          >
            {saving && <i className="fa fa-spinner fa-spin me-2" />}
            {isEdit ? "Update Syllabus" : "Save"}
          </Button>
        </div>
      )}
    </form>
  );
};
export default SyllabusForm;  
